import { PUB_SUB_EVENTS } from '@/scripts/core/global'
import { createVariantChangeEvent, publish } from '@/scripts/core/global'
import {
	getAttributeOrThrow,
	qsaOptional,
	qsaRequired,
	qsOptional,
	qsRequired,
} from '@/scripts/core/global'
import { type ProductVariant } from '@/scripts/types/api'
import { type uCoastWindow } from '@/scripts/setup'
import { type ProductForm } from '@/scripts/product/product-form'
import { type ShareButton } from '@/scripts/optional/share-button'
import { type PickupAvailability } from '@/scripts/optional/pickup-availability'
import { UcoastEl } from '@/scripts/core/UcoastEl'
import { type ProductStickyCta } from '@/scripts/product/product-sticky-cta'
import { type ProductInfo } from '@/scripts/product/product-info'
import { BasicAccordion } from "@/scripts/theme/basic-accordion"

declare let window: uCoastWindow

export class VariantSelects extends UcoastEl {
	static htmlSelector = 'variant-selects'
	static selectors = {
		sliderContainer: '',
		sliderTrack: '',
		sliderLabels: '',
		sliderInputs: '',
		sliderNextbutton: '',
		sliderPrevbutton: '',
		closeModalButton: '',
	}
	instanceSelectors = VariantSelects.selectors
	currentVariant?: ProductVariant
	options?: string[]
	variantData?: ProductVariant[]
	isQuickAdd: boolean
	source: string
	productId: string
	parentComponent?: ProductInfo | ProductStickyCta
	externalSource: string | undefined
	constructor() {
		super()
		this.setInstanceSelectors()
		this.addEventListener('change', this.onVariantChange.bind(this))
		this.isQuickAdd = false
		this.source = getAttributeOrThrow('data-source', this)
		this.productId = getAttributeOrThrow('data-uc-product-id', this)
		this.parentComponent = this.getParentComponent()
	}

	getParentComponent() {
		if (this.source !== 'sticky' && this.source !== 'primary') return undefined
		return this.source === 'sticky'
			? qsOptional<ProductStickyCta>(
					`product-sticky-cta[data-uc-product-id="${this.productId}"]`
			  )
			: qsOptional<ProductInfo>(`product-info[data-uc-product-id="${this.productId}"]`)
	}

	formatShopifyPrice(price: number, comparePrice?: number): string {
		if (comparePrice && comparePrice > price) {
			const comparePriceString =
				comparePrice % 100 === 0
					? `$${(comparePrice / 100).toFixed(0)}`
					: `$${(comparePrice / 100).toFixed(2)}`
			const priceString =
				price % 100 === 0 ? `$${(price / 100).toFixed(0)}` : `$${(price / 100).toFixed(2)}`
			return `<s>${comparePriceString}</s> ${priceString}`
		} else {
			return price % 100 === 0
				? `$${(price / 100).toFixed(0)}`
				: `$${(price / 100).toFixed(2)}`
		}
	}

	setInstanceSelectors() {
		this.instanceSelectors = VariantSelects.selectors
	}

	onVariantChange(_: Event, fromExternalSource = false) {
		this.updateOptions()
		this.updateMasterId()
		this.toggleAddButton(true, '', false)
		this.updatePickupAvailability()
		this.removeErrorMessage()
		this.updateVariantStatuses()

		if (!this.currentVariant) {
			this.toggleAddButton(true, '', true)
			this.setUnavailable()
		} else {
			this.updateMedia()
			this.updateURL()
			this.updateVariantInput()
			this.renderProductInfo()
			this.updateShareUrl()
		}
		if (this.parentComponent && !fromExternalSource) {
			this.parentComponent.updateSiblingForm()
		}
	}

	updateOptions() {
		this.options = Array.from(
			qsaRequired<HTMLSelectElement>('select', this),
			(select) => select.value
		)
	}

	updateMasterId() {
		this.currentVariant = this.getVariantData().find((variant) => {
			return !variant.options
				.map((option, index) => {
					return this.options && this.options[index] === option
				})
				.includes(false)
		})
	}

	updateMedia() {
		const currentVariant = this.currentVariant
		const variantImages = qsaOptional('[data-uc-variant-image]')
		if (!variantImages) {
			return
		}
		if (!currentVariant) return
		if (!currentVariant.featured_image_id) return
		variantImages.forEach((el) => {
			const variantIdString = el.getAttribute('data-uc-variant-image')
			if (!variantIdString) return
			const variantId = parseInt(variantIdString)
			if (variantId === currentVariant.id) {
				el.classList.add('active')
			} else {
				el.classList.remove('active')
			}
		})
	}

	updateVariantData() {}

	updateURL() {
		if (!this.currentVariant || this.dataset.updateUrl === 'false') return
		window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`)
	}

	updateShareUrl() {
		if (!this.currentVariant) throw new Error('cannot updateShareUrl without currentVariant')
		const shareButton = qsOptional<ShareButton>(`#Share-${this.dataset.section}`)
		if (!shareButton || !shareButton.updateUrl) return
		shareButton.updateUrl(
			`${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`
		)
	}

	updateVariantInput() {
		const currentVariant = this.currentVariant
		if (!currentVariant) throw new Error('cannot updateVariantInput without currentVariant')
		const productForms = qsaOptional(
			`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
		)
		productForms?.forEach((productForm) => {
			const input = qsRequired<HTMLInputElement>('input[name="id"]', productForm)
			input.value = `${currentVariant.id}`
			input.dispatchEvent(new Event('change', { bubbles: true }))
		})
	}

	getVariantOptionFromIndex(variant: ProductVariant, index: number) {
		switch (index) {
			case 1:
				return variant.option1
			case 2:
				return variant.option2
			case 3:
				return variant.option3
			default:
				throw new Error('invalid index')
		}
	}

	updateVariantStatuses() {
		if (!this.variantData) throw new Error('cannot updateVariantStatuses without variantData')
		const selectedOptionOneVariants = this.variantData.filter(
			(variant) =>
				qsRequired<HTMLInputElement | HTMLOptionElement>(':checked', this).value ===
				variant.option1
		)
		const inputWrapperNodes = qsaOptional(
			'.product-form__input:not([data-uc-fieldset-color])',
			this
		)
		const inputWrappers = inputWrapperNodes?.length ? Array.from(inputWrapperNodes) : []
		if (inputWrappers.length) {
			inputWrappers.forEach((option: HTMLElement, index: number) => {
				if (index === 0) return
				const optionInputNodes = qsaOptional<HTMLInputElement | HTMLOptionElement>(
					'input[type="radio"], option',
					option
				)
				const optionInputs = optionInputNodes?.length ? Array.from(optionInputNodes) : []

				const previousOptionSelected = qsRequired<HTMLInputElement | HTMLOptionElement>(
					':checked',
					inputWrappers[index - 1]
				).value
				const availableOptionInputsValue = selectedOptionOneVariants
					.filter(
						(variant) =>
							variant.available &&
							this.getVariantOptionFromIndex(variant, index) ===
								previousOptionSelected
					)
					.map((variantOption) =>
						this.getVariantOptionFromIndex(variantOption, index + 1)
					)
				this.setInputAvailability(optionInputs, availableOptionInputsValue)
			})
		} else {
			console.warn('no input wrappers found')
		}
	}

	setInputAvailability(
		listOfOptions: (HTMLInputElement | HTMLOptionElement)[],
		listOfAvailableOptions: (string | null)[]
	) {
		listOfOptions.forEach((input) => {
			if (listOfAvailableOptions.includes(input.getAttribute('value'))) {
				input.innerText = getAttributeOrThrow('value', input)
			} else {
				input.innerText = window.variantStrings.unavailable_with_option.replace(
					'[value]',
					getAttributeOrThrow('value', input)
				)
			}
		})
	}

	updatePickupAvailability() {
		const pickUpAvailability = qsOptional<PickupAvailability>('pickup-availability')
		if (!pickUpAvailability) return
		const currentVariant = this.currentVariant

		if (currentVariant && currentVariant.available) {
			pickUpAvailability.fetchAvailability(`${currentVariant.id}`)
		} else {
			pickUpAvailability.removeAttribute('available')
			pickUpAvailability.innerHTML = ''
		}
	}

	removeErrorMessage() {
		const section = this.closest('section')
		if (!section) return

		const productForm = qsOptional<ProductForm>('product-form', section)
		if (productForm) productForm.handleErrorMessage()
	}

	renderProductInfo() {
		if (!this.currentVariant)
			throw new Error('this.currentVariant not selected, renderProductInfo called too early')
		const requestedVariantId = this.currentVariant.id
		const sectionId = this.dataset.originalSection
			? this.dataset.originalSection
			: getAttributeOrThrow('data-section', this)

		fetch(
			`${this.dataset.url}?variant=${requestedVariantId}&section_id=${
				this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section
			}`
		)
			.then((response) => response.text())
			.then((responseText) => {
				if (!this.currentVariant)
					throw new Error(
						'this.currentVariant not selected, renderProductInfo called too early'
					)
				// prevent unnecessary ui changes from abandoned selections
				if (this.currentVariant.id !== requestedVariantId) return

				const html = new DOMParser().parseFromString(responseText, 'text/html')
				const destination = document.getElementById(`price-${this.dataset.section}`)
				const source = html.getElementById(
					`price-${
						this.dataset.originalSection
							? this.dataset.originalSection
							: this.dataset.section
					}`
				)
				const skuSource = html.getElementById(
					`Sku-${
						this.dataset.originalSection
							? this.dataset.originalSection
							: this.dataset.section
					}`
				)
				const skuDestination = document.getElementById(`Sku-${this.dataset.section}`)
				const inventorySource = qsOptional(
					`#Inventory-${
						this.dataset.originalSection
							? this.dataset.originalSection
							: this.dataset.section
					}`,
					html
				)
				const inventoryDestination = document.getElementById(
					`Inventory-${this.dataset.section}`
				)

				if (source && destination) destination.innerHTML = source.innerHTML
				if (inventorySource && inventoryDestination)
					inventoryDestination.innerHTML = inventorySource.innerHTML
				if (skuSource && skuDestination) {
					skuDestination.innerHTML = skuSource.innerHTML
					skuDestination.classList.toggle(
						'visibility-hidden',
						skuSource.classList.contains('visibility-hidden')
					)
				}

				const price = document.getElementById(`price-${this.dataset.section}`)

				if (price) price.classList.remove('visibility-hidden')

				if (inventoryDestination)
					inventoryDestination.classList.toggle(
						'visibility-hidden',
						inventorySource?.innerText === ''
					)

				const addButtonUpdated = html.getElementById(`ProductSubmitButton-${sectionId}`)
				this.toggleAddButton(
					addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true,
					window.variantStrings.soldOut
				)

				// variant options updates

				const customProductTabs = qsaOptional<BasicAccordion>('[data-uc-product-tab-id]', html)
				customProductTabs?.forEach((tab) => {
					const tabId = getAttributeOrThrow('data-uc-product-tab-id', tab)
					const relatedTab = qsRequired<BasicAccordion>(`[data-uc-product-tab-id="${tabId}"]`)
					relatedTab.innerHTML = tab.innerHTML
					const style = tab.getAttribute('style')
					if (style) {
						relatedTab.setAttribute('style', style)
					} else {
						relatedTab.removeAttribute('style')
					}
					relatedTab.reInit()
					relatedTab.close()
				})


				// icons

				const customIcons = qsOptional('[data-uc-custom-icon-data]', html)
				if (customIcons) {
          const destination = qsRequired('[data-uc-custom-icon-data]')
					destination.innerHTML = customIcons.innerHTML
				}
        const customIconsMobile = qsOptional('[data-uc-custom-icon-data-mobile]', html)
				if (customIconsMobile) {
					const destinationMobile = qsRequired('[data-uc-custom-icon-data-mobile]')
					destinationMobile.innerHTML = customIconsMobile.innerHTML
				}

				publish(
					PUB_SUB_EVENTS.variantChange,
					createVariantChangeEvent({
						source: 'product-form',
						data: {
							sectionId,
							html,
							variant: this.currentVariant,
						},
					})
				)
			})
	}

	toggleAddButton(_disable = true, text: string, modifyClass = true) {
		// Find both buttons (they both exist now in HTML)
		const klaviyoButton = qsOptional('[data-uc-klaviyo-oos-popup-trigger]', document)
		const formContainer = qsOptional('#form-container', document)
		const addButton = qsOptional('[name="add"]', document)
		const priceEl = qsOptional('[name="add"] > span[data-uc-price-variable]', document)
		const addButtonText = qsOptional('[name="add"] > span[data-uc-inventory-status-text]', document)
		const preorderText = qsOptional('[data-uc-preorder-text]')

		if (this.currentVariant && !this.currentVariant.available) {
			// Show Klaviyo button, hide form
			if (klaviyoButton) {
				klaviyoButton.classList.remove('hidden')
				// Update variant ID in Klaviyo button
				klaviyoButton.setAttribute('data-uc-klaviyo-oos-popup-trigger', `${this.currentVariant.id}`)
			}
			if (formContainer) {
				formContainer.classList.add('hidden')
			}
			if (preorderText) preorderText.innerHTML = ''
		} else {
			// Show form, hide Klaviyo button
			if (klaviyoButton) {
				klaviyoButton.classList.add('hidden')
			}
			if (formContainer) {
				formContainer.classList.remove('hidden')
			}
			
			// Update the add button based on variant status
			if (addButton && addButtonText) {
				this.updateAddButtonState(addButton, addButtonText, preorderText || null, text)
			}
		}

		if (priceEl && this.currentVariant) {
			const priceString = this.formatShopifyPrice(this.currentVariant.price, this.currentVariant.compare_at_price)
			priceEl.innerHTML = priceString
		}

		if (this.parentComponent && qsOptional('[data-uc-variant-name]', this.parentComponent) && this.currentVariant) {
			const variantName = qsRequired<HTMLElement>('[data-uc-variant-name]', this.parentComponent)
			variantName.innerHTML = this.currentVariant.title
		}

		if (!modifyClass) return
	}

	setUnavailable() {
		const button = qsRequired(`#product-form-${this.dataset.section}`)
		const addButton = qsOptional('[name="add"]', button)
		const addButtonText = qsOptional('[name="add"] > span', button)
		const price = document.getElementById(`price-${this.dataset.section}`)
		const inventory = document.getElementById(`Inventory-${this.dataset.section}`)
		const sku = document.getElementById(`Sku-${this.dataset.section}`)

		if (!addButton || !addButtonText) return
		addButtonText.textContent = window.variantStrings.unavailable
		if (price) price.classList.add('visibility-hidden')
		if (inventory) inventory.classList.add('visibility-hidden')
		if (sku) sku.classList.add('visibility-hidden')
	}

	getVariantData(): ProductVariant[] {
		const textContent = qsRequired<HTMLScriptElement>(
			'[type="application/json"]',
			this
		).textContent
		if (!textContent) throw new Error('Variant data not found')
		const updatedVariantData = this.variantData ?? JSON.parse(textContent)
		this.variantData = updatedVariantData
		return updatedVariantData
	}

	getSelectedOptions(): { name: string; value: string }[] {
		const fieldsetNodes = qsaOptional<HTMLFieldSetElement>('fieldset', this)
		if (!fieldsetNodes)
			throw new Error('cannot do updateOptions in variant-radios, no fieldset nodes found')
		const fieldsets = Array.from(fieldsetNodes).filter(
			(fieldset) => !fieldset.hasAttribute('data-uc-fieldset-color')
		)

		return fieldsets.map((fieldset) => {
			const inputNodes = qsaOptional<HTMLInputElement>('input', fieldset)
			if (!inputNodes)
				throw new Error('cannot do updateOptions in variant-radios, no input nodes found')
			const inputs = Array.from(inputNodes)
			const checkedInput = inputs.find((radio) => radio.checked)
			if (!checkedInput)
				throw new Error('cannot do updateOptions in variant-radios, no checked input found')

			return {
				name: getAttributeOrThrow('name', checkedInput),
				value: checkedInput.value,
			}
		})
	}

	updateOptionsFromExternalSource(options: { name: string; value: string }[]) {
		options.forEach((option) => {
			const selectedInput = qsRequired<HTMLInputElement>(
				`input[name="${option.name}"][value="${option.value}"]`,
				this
			)
			selectedInput.checked = true
		})
		this.onVariantChange(new Event('externalVariantUpdate'), true)
	}

	private updateAddButtonState(addButton: HTMLElement, addButtonText: HTMLElement, preorderText: HTMLElement | null, text: string) {
		addButton.removeAttribute('data-uc-klaviyo-oos-popup-trigger')

		if (this.currentVariant?.inventory_status === 'OutOfStock') {
			addButton.setAttribute('type', 'submit')
			addButton.setAttribute('disabled', 'disabled')
			if (text) addButtonText.textContent = text
			if (preorderText) preorderText.innerHTML = ''
			addButton.classList.add('visually-disabled')
		} else if (this.currentVariant?.inventory_status === 'PreOrder') {
			addButton.setAttribute('type', 'submit')
			addButton.removeAttribute('disabled')
			addButtonText.textContent = this.currentVariant.inventory_status_text
			if (preorderText) preorderText.innerHTML = (this.currentVariant as any).preorder_text || ''
			addButton.classList.remove('visually-disabled')
		} else if (this.isQuickAdd) {
			addButton.setAttribute('type', 'submit')
			addButton.removeAttribute('disabled')
			addButtonText.textContent = window.variantStrings.quickAdd
			if (preorderText) preorderText.innerHTML = ''
			addButton.classList.remove('visually-disabled')
		} else {
			// Normal add to cart button
			addButton.setAttribute('type', 'submit')
			addButton.removeAttribute('disabled')
			addButtonText.textContent = window.variantStrings.addToCart
			if (preorderText) preorderText.innerHTML = ''
			addButton.classList.remove('visually-disabled')
		}
	}
}
